import { useCallback } from 'react';

import { type Upload, uploadByIdSelector, uploadedPartsSelector, useUploaderStore } from '@air/redux-uploader';

import { getChunksCountForLargeFile } from '../utilities';

const hasAllMultipartParts = ({
  totalChunks,
  uploadedChunks,
}: {
  totalChunks: number;
  uploadedChunks: { PartNumber: number }[];
}) => {
  if (totalChunks <= 0 || uploadedChunks.length !== totalChunks) {
    return false;
  }

  const uploadedPartNumbers = new Set(uploadedChunks.map(({ PartNumber }) => PartNumber));

  if (uploadedPartNumbers.size !== totalChunks) {
    return false;
  }

  return Array.from({ length: totalChunks }, (_, index) => index + 1).every((partNumber) =>
    uploadedPartNumbers.has(partNumber),
  );
};

export const useLargeUploadCanBeCompleted = () => {
  const store = useUploaderStore();

  const largeUploadCanBeCompleted = useCallback(
    (uploadId: Upload['id']) => {
      const upload = uploadByIdSelector(store.getState(), uploadId);

      if (upload) {
        const uploadedChunks = uploadedPartsSelector(store.getState(), uploadId);
        const totalChunks = getChunksCountForLargeFile(upload);

        return hasAllMultipartParts({ totalChunks, uploadedChunks });
      }

      return false;
    },
    [store],
  );

  return {
    largeUploadCanBeCompleted,
  };
};
